import React, {useCallback, useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Grid,
    IconButton,
    Modal,
    ModalActions,
    ModalContent,
    ModalTitle,
    Tooltip,
    Typography,
    useToastProvider
} from "@deere/fuel-react";
import {
    INVALID_USERS_TXT,
    MANAGE_SH_FEEDBACK_USERS_ERROR,
    MANAGE_SH_USER_ERROR,
    MANAGE_STAKEHOLDERS_ALERT_DESC,
    MANAGE_STAKEHOLDERS_ALERT_TITLE, MANAGE_STAKEHOLDERS_DELETE_TOOLTIP,
    MANAGE_STAKEHOLDERS_DESCRIPTION,
    MANAGE_STAKEHOLDERS_TEXT
} from "../Constants";
import {DataGrid} from '@mui/x-data-grid';
import {UserSearch} from "../UserSearch";
import {useOktaAuth} from '@okta/okta-react';
import {useStyles} from "../styles/ManageStakeholders";
import PersonIcon from '@material-ui/icons/Person';
import {useAuthConfig} from "../helper";
import {Loading} from "../Loading";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from "@material-ui/icons/Delete";
import WarningIcon from "@material-ui/icons/Warning";
import ReportIcon from "@material-ui/icons/Report";
import {getOptionSelected} from "../utils/autocompleteUtils";

const filteredRows = (list) => {
    return list.filter((ele) => ele.status !== "D");
};

export const ManageStakeholdersModal = ({campaignId, onSave, supplierNumber}) => {
    const {authState} = useOktaAuth();
    const classes = useStyles();
    const {enqueueToast} = useToastProvider();
    let authAxios = useAuthConfig();
    const [open, setOpen] = useState(false);
    const [stakeholdersData, setStakeholdersData] = useState([]);
    const [feedbackUsers, setFeedbackUsers] = useState([]);
    const [submittedUsers, setSubmittedUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isAnyInvalid, setIsAnyInvalid] = useState(false);
    const [roleList, setRoleList] = useState([]);
    const [unitList, setUnitList] = useState([]);
    const [error, setError] = useState("");

    const handleClickOpen = () => {
        setOpen(true);
        fetchStakeholdersData()
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSave = () => {
        const updatedRows = stakeholdersData.map((row) => {
            row.validUser = !feedbackUsers.includes(row.user.racfID);
            return row;
        });
        const filteredRows = updatedRows.filter((row) => {
            return row.user.racfID !== "" || row.roles !== "" || row.units !== ""
        });
        const isUserEmpty = filteredRows.every(item => item.user.description !== "")
        const isRoleEmpty = filteredRows.every(item => item.roles !== "")
        const isUnitEmpty = filteredRows.every(item => item.units !== "")
        const notValidUsers = filteredRows.filter(item => !item.validUser && !(item.status == null || item.status === "D"))
        const notValidUsersList = [...new Map(notValidUsers.map(item => [item["user"]["racfID"], item["user"]["displayName"]])).values()]

        if (notValidUsers.length > 0) {
            setError(`${notValidUsersList.join(', ')} ${notValidUsers.length === 1 ? "has" : "have"} ${MANAGE_SH_FEEDBACK_USERS_ERROR}`)
        } else if (!isUserEmpty || !isRoleEmpty || !isUnitEmpty) {
            setError(MANAGE_SH_USER_ERROR)
        } else {
            setOpen(false);
            onSave(filteredRows)
        }
    }

    const getStakeholdersList = async () => {
        setIsLoading(true)
        try {
            return await authAxios.get(`/campaign/manage/${campaignId}`)
        } catch (error) {
            console.error('Error fetching stakeholders:', error);
            enqueueToast({
                variant: "error",
                title: "Error fetching stakeholders",
                isCloseable: true
            });
        }
    };

    const getFunctionalGroup = async () => {
        try {
            return await authAxios.get(`/supplierDetail/${supplierNumber}`);
        } catch (error) {
            console.error('Error fetching Supplier Details:', error);
            enqueueToast({
                variant: "error",
                title: "Error fetching roles",
                isCloseable: true
            });
        }
    };

    const getRoles = async (functionalCode = "All") => {
        try {
            return await authAxios.get(`/stakeholders?functionalAreaCodes=${functionalCode}`);
        } catch (error) {
            console.error('Error fetching roles:', error);
            enqueueToast({
                variant: "error",
                title: "Error fetching roles",
                isCloseable: true
            });
        }
    };

    const getUnits = async () => {
        try {
            return await authAxios.get(`/units`);
        } catch (error) {
            console.error('Error fetching units:', error);
            enqueueToast({
                variant: "error", 
                title: "Error fetching units",
                isCloseable: true
            });
        }
    };

    const handleSelectedUser = useCallback(
        (value, id) => {
            setError("");
            const updatedRows = stakeholdersData.map((row) => {
                if (row.id === id) {
                    const descObj = {description: value ? `${value.lastName},${value.firstName}-${value.racfID}` : ""}
                    row.user = {...value, ...descObj};
                    row.status = row.status === "A" ? "A" : "U"
                    row.validUser = true
                }
                return row;
            });
            setStakeholdersData(updatedRows);
        },
        [stakeholdersData]
    );

    const handleRoleChange = useCallback(
        (value, id) => {
            setError("");
            const updatedRows = stakeholdersData.map((row) => {
                if (row.id === id) {
                    row.roles = {...value}
                    row.status = row.status === "A" ? "A" : "U"
                }
                return row;
            });
            setStakeholdersData(updatedRows);
        },
        [stakeholdersData]
    );

    const handleUnitChange = useCallback(
        (value, id) => {
            const updatedRows = stakeholdersData.map((row) => {
                if (row.id === id) {
                    row.units = {...value}
                    row.status = row.status === "A" ? "A" : "U"
                }
                return row;
            });
            setStakeholdersData(updatedRows);
        },
        [stakeholdersData]
    );

    const handleStakeholderAdd = () => {
        let obj = {
            roles: "",
            units: "",
            user: {
                description: "",
                firstName: "",
                lastName: "",
                racfID: "",
            },
            status: "A",
            valid: true,
            validUser: true
        }
        const idObj = {id: Date.now() + Math.random()}
        obj = {...idObj, ...obj};
        setStakeholdersData([...stakeholdersData, obj])
    }
    const handleReopen = async (feedbackUserId) => {
        await authAxios.put(`/feedback/${campaignId}/${feedbackUserId}/reopen`)
            .then(res => {
                enqueueToast({
                    variant: "success",
                    title: res.data,
                    isCloseable: true
                });
                fetchStakeholdersData();
            }).catch((error) => {
                console.error('Error reopening feedback:', error);
                enqueueToast({
                    variant: "error",
                    title: "Error Reopening Feedback",
                    message: error.response.data,
                    isPersistent: true
                });
            });
    }

    const handleRowDelete = useCallback(
        (id) => {
            const updatedRows = stakeholdersData.map((row) => {
                if (row.id === id) {
                    row.status = row.status === "A" ? "ND" : "D"
                }
                return row;
            });
            const filterUpdatedRows = updatedRows.filter(ele => ele.status !== "ND");
            setStakeholdersData(filterUpdatedRows);
        },
        [stakeholdersData]
    );

    const rowsData = filteredRows(stakeholdersData) && filteredRows(stakeholdersData).map((item) => {
        return {
            id: item.id,
            user: item.user,
            role: item.roles,
            unit: item.units,
            status: item.status,
            valid: item.valid,
            validUser: item.validUser
        };
    });

    const columns = [
        {
            field: 'id',
            headerName: 'ID',
            hide: true
        },
        {
            field: 'user',
            headerName: 'Stakeholder',
            flex: 1,
            renderCell: (params) => {
                return (
                    <>
                        <Box display="inline">
                            {!params.row.valid && (
                                <WarningIcon style={{fill: "#fab64c"}}/>
                            )}
                            {!params.row.validUser && (
                                <ReportIcon fontSize="medium" style={{
                                    fill: "#c21020",
                                    marginRight: "0.25rem",
                                    marginTop: "0.1rem"
                                }} display="inline"/>
                            )}
                        </Box>&nbsp;
                        <UserSearch
                            defValue={params.row.user}
                            onOptionsChange={handleSelectedUser}
                            rowID={params.row.id}/>
                    </>
                )
            }
        },
        {
            field: 'role',
            headerName: 'Role',
            flex: 1,
            renderCell: (params) => {
                if (params.row.status === "A") {
                    return (
                        <Autocomplete
                            fullWidth
                            size="small"
                            id="role-find"
                            options={roleList}
                            defaultValue={params.row.role}
                            value={params.row.role}
                            getOptionLabel={(option) => option.description}
                            getOptionSelected={getOptionSelected}
                            onChange={(e, value) => {
                                handleRoleChange(value, params.row.id)
                            }}
                            renderInput={(params) => <TextField
                                {...params}
                                placeholder="Select Role"
                                variant="outlined"
                            />}
                        />
                    )
                } else {
                    return (
                        <Typography variant="body1" fontWeight="semibold">
                            {params.row.role.description}
                        </Typography>
                    );
                }
            }
        },
        {
            field: 'unit',
            headerName: 'Unit',
            flex: 1,
            renderCell: (params) => {
                if (params.row.status === "A") {
                    return (
                        <Autocomplete
                            fullWidth
                            size="small"
                            id="unit-find"
                            options={unitList}
                            defaultValue={params.row.unit}
                            value={params.row.unit}
                            getOptionLabel={(option) => option.description}
                            getOptionSelected={getOptionSelected}
                            onChange={(e, value) => {
                                handleUnitChange(value, params.row.id)
                            }}
                            renderInput={(params) => <TextField {...params}
                                                                placeholder="Select Unit"
                                                                variant="outlined"/>}
                        />

                    )
                } else {
                    return (
                        <Typography variant="body1" fontWeight="semibold">
                            {params.row.unit.description}
                        </Typography>
                    );
                }
            }
        },
        {
            field: 'actions',
            headerName: 'Action',
            flex: .6,
            renderCell: (params) => {
                return (
                    <>
                        <Button
                            variant="primary" size="large"
                            onClick={() => handleReopen(params.row.user.racfID)}
                            disabled={!submittedUsers.includes(params.row.user.racfID)}
                        >
                            Reopen
                        </Button>
                        <Tooltip title={feedbackUsers.includes(params.row.user.racfID) ? MANAGE_STAKEHOLDERS_DELETE_TOOLTIP : ""}>
                            <IconButton
                                aria-label="delete" edge="end"
                                onClick={() => handleRowDelete(params.row.id)}
                                disabled={feedbackUsers.includes(params.row.user.racfID)}
                            >
                                <DeleteIcon/>
                            </IconButton>
                        </Tooltip>
                    </>

                )
            }
        }
    ];

    const fetchStakeholdersData = () => {
        setError("")
        getStakeholdersList().then(res => {
            const updatedRows = res.data.campaignStakeholder.map((row) => {
                row.validUser = true
                return row;
            });
            setStakeholdersData(updatedRows);
            setFeedbackUsers(res.data.feedbackUserList)
            setSubmittedUsers(res.data.submittedUserList)
            const validArr = updatedRows && updatedRows.map(item => item.valid)
            setIsAnyInvalid(!validArr.every(Boolean))
            setIsLoading(false)
        }).catch((error) => {
            setIsLoading(false);
            console.error('Error in fetchStakeholdersData:', error);
        });
    }

    useEffect(() => {
        fetchStakeholdersData();

        // fetch functional group first to get the roles based on functional code
        getFunctionalGroup().then(res => {
              const functionalCode = res.data.functionalAreaType || "All";
              return getRoles(functionalCode)
        }).then(res => {
            setRoleList(res.data);
        });
        getUnits().then(res => {
            setUnitList(res.data);
        });
        return () => {
            setRoleList([]);
            setUnitList([]);
            setStakeholdersData([]);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState]);

    return (
        <div>
            <Button variant="text" onClick={handleClickOpen}>
                <PersonIcon/>&nbsp;{MANAGE_STAKEHOLDERS_TEXT}
            </Button>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-Modal-title"
                aria-describedby="alert-Modal-description"
                maxWidth="md"
                fullWidth={true}
            >
                <ModalTitle onClose={handleClose} id="alert-Modal-title">
                    {MANAGE_STAKEHOLDERS_TEXT}
                </ModalTitle>
                <ModalContent>
                    {isLoading && (
                        <Loading/>
                    )}
                    {!isLoading && (
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={12}>
                                <Typography size="md" component="p" gutterBottom>
                                    {MANAGE_STAKEHOLDERS_DESCRIPTION}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Alert
                                    variant={"info"}
                                    title={MANAGE_STAKEHOLDERS_ALERT_TITLE}>{MANAGE_STAKEHOLDERS_ALERT_DESC}</Alert>
                            </Grid>
                            <Grid item xs={12}>
                                <Box m={1} style={{height: 300}}>
                                    <DataGrid
                                        className={classes.root}
                                        rows={rowsData}
                                        columns={columns}
                                        hideFooter
                                        checkboxSelection={false}
                                        disableSelectionOnClick
                                        headerHeight={0}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-start">
                                    <Typography color="error" size="xs">
                                        {error}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </ModalContent>
                <ModalActions>
                    <Grid container>
                        <Grid item xs={5}>
                            <Box display="flex" justifyContent="flex-start">
                                <Button variant="secondary" onClick={handleStakeholderAdd} startIcon={<AddIcon/>}> Add
                                    Stakeholder </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={7}>
                            <Box display="flex" justifyContent="flex-end">
                                {isAnyInvalid && (
                                    <Box mr={1}>
                                        <Typography variant="label" display="inline">
                                            <WarningIcon style={{fill: "#fab64c"}}/> &nbsp; {INVALID_USERS_TXT} &nbsp;
                                        </Typography>
                                    </Box>
                                )}
                                <Button onClick={handleClose} variant="secondary">
                                    Cancel
                                </Button> &nbsp; &nbsp;
                                <Button onClick={handleSave} variant="primary">
                                    Update
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </ModalActions>
            </Modal>
        </div>
    );
};
